import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { apiGet, apiPost } from '../../../src/api/client';
import Card from '../../../src/components/Card';
import Button from '../../../src/components/Button';
import { useToast } from '../../../src/components/Toast';
import ContentContainer from '../../../src/components/ContentContainer';
import { useTheme } from '../../../src/context/ThemeContext';
import type { ColorTheme } from '../../../src/utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../src/utils/theme';

interface RedemptionEntry {
  points: number;
  tierName: string | null;
  freeMinutes: number | null;
  vacMinutes?: number | null;
  vendCents?: number | null;
}

/** Human label for a redemption row — exactly one field is set per row. */
function entryLabel(entry: RedemptionEntry): string {
  if (entry.vacMinutes) return `${entry.vacMinutes} vacuum minutes`;
  if (entry.vendCents) return `$${(entry.vendCents / 100).toFixed(2)} vending credit`;
  if (entry.freeMinutes) return `${entry.freeMinutes} self-serve minutes`;
  return `Free ${entry.tierName} wash`;
}

/** Where the minted voucher gets used, for the success toast. */
function entryUseHint(entry: RedemptionEntry): string {
  if (entry.vacMinutes) return 'use at a vacuum';
  if (entry.vendCents) return 'use at a vending machine';
  return 'use at your next wash';
}
interface PointsBalance {
  ownerId: string;
  ownerName: string;
  programName: string;
  balance: number;
  lifetimeEarned: number;
  redemptionTable: RedemptionEntry[];
}

export default function RedeemPointsScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const router = useRouter();
  const toast = useToast();
  const [balances, setBalances] = useState<PointsBalance[]>([]);
  const [redeeming, setRedeeming] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await apiGet<{ balances: PointsBalance[] }>('/api/me/points');
        setBalances(r.balances);
      } catch (err: any) {
        toast.show({ type: 'error', title: 'Error', message: err.message });
      }
    })();
  }, []);

  const handleRedeem = async (ownerId: string, idx: number, entry: RedemptionEntry) => {
    setRedeeming(`${ownerId}:${idx}`);
    try {
      // Voucher may carry freeVendCents + appliesTo for the new vacuum/vending
      // rows; the wash/vend pay screens pick it up from /api/me/vouchers.
      const res = await apiPost<{
        voucher: {
          code: string; discountKind: string; freeVendCents?: number | null;
          appliesTo?: { tierIds: string[]; selfServe: boolean; vacuum?: boolean; vending?: boolean } | null;
        };
        remainingPoints: number;
      }>('/api/points/redeem', {
        ownerId, redemptionIndex: idx,
      });
      toast.show({ type: 'success', title: 'Redeemed!', message: `Code ${res.voucher.code} — ${entryUseHint(entry)}.` });
      // refresh balances
      const r = await apiGet<{ balances: PointsBalance[] }>('/api/me/points');
      setBalances(r.balances);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setRedeeming(null);
    }
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <ContentContainer>
        {balances.length === 0 ? (
          <Card style={{ padding: spacing.xl, alignItems: 'center' }}>
            <Text style={{ color: colors.textSecondary, textAlign: 'center' }}>
              No rewards balances yet. Visit a participating car wash and pay for a wash to start earning.
            </Text>
          </Card>
        ) : balances.map(b => (
          <View key={b.ownerId} style={{ marginBottom: spacing.lg }}>
            <Card style={[styles.summary, { backgroundColor: colors.primaryDark }]}>
              <Text style={styles.summaryLabel}>{b.programName}</Text>
              <Text style={styles.summaryAmount}>{b.balance}</Text>
              <Text style={styles.summarySub}>points</Text>
            </Card>
            {b.redemptionTable.length === 0 ? (
              <Text style={styles.hintText}>This owner hasn't set redemption options yet.</Text>
            ) : (
              b.redemptionTable.map((entry, idx) => {
                const canRedeem = b.balance >= entry.points;
                const label = entryLabel(entry);
                return (
                  <Card key={idx} style={[styles.row, !canRedeem && { opacity: 0.6 }]}>
                    <Ionicons name="star" size={24} color={colors.primary} style={{ marginRight: spacing.md }} />
                    <View style={{ flex: 1 }}>
                      <Text style={styles.rowTitle}>{label}</Text>
                      <Text style={styles.rowSub}>{entry.points} points</Text>
                    </View>
                    <Button
                      title={canRedeem ? 'Redeem' : 'Not enough'}
                      size="sm"
                      variant={canRedeem ? 'primary' : 'outline'}
                      onPress={() => canRedeem && handleRedeem(b.ownerId, idx, entry)}
                      loading={redeeming === `${b.ownerId}:${idx}`}
                      disabled={!canRedeem || !!redeeming}
                    />
                  </Card>
                );
              })
            )}
          </View>
        ))}
        <View style={{ height: spacing.xxl }} />
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.md },
  summary: { alignItems: 'center', padding: spacing.lg, marginBottom: spacing.md },
  summaryLabel: { fontSize: fontSize.sm, color: 'rgba(255,255,255,0.8)' },
  summaryAmount: { fontSize: 48, fontWeight: fontWeight.bold as any, color: c.white, marginTop: spacing.xs },
  summarySub: { fontSize: fontSize.xs, color: 'rgba(255,255,255,0.7)' },
  row: { flexDirection: 'row', alignItems: 'center', padding: spacing.md, marginBottom: spacing.sm },
  rowTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold as any, color: c.text },
  rowSub: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  hintText: { fontSize: fontSize.sm, color: c.textLight, fontStyle: 'italic', marginVertical: spacing.md, textAlign: 'center' },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
